import { createApi } from "@reduxjs/toolkit/query/react";
import fetchBaseQueryCSRF from "../helper";
import { GA4ConversionEvent, GADSCampaign, GADSCampaignConversion, GoogleAuth, GoogleAuthList } from "./models";

export enum GOOGLE_ANALYTICS_SCOPES {
    ga4 = "https://www.googleapis.com/auth/analytics.readonly",
    gsc = "https://www.googleapis.com/auth/webmasters",
    gads = "https://www.googleapis.com/auth/adwords",
}

const googleAPI = createApi({
    reducerPath: "googleAPI",
    baseQuery: fetchBaseQueryCSRF({ baseUrl: `${CROSS_API_URL}/google/auth` }),
    endpoints: (builder) => ({
        getGoogleAccount: builder.query<GoogleAuth, string>({
            query: (bqTable) => ({
                url: `/?bqTable=${bqTable}`,
                method: "GET",
            }),
        }),
        getGoogleAuthList: builder.query<GoogleAuthList, string>({
            query: (token) => ({
                url: `/objects/list`,
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
        }),
        getGoogleAuthObjectsStatus: builder.query<{ status: string }, string>({
            query: (id) => ({
                url: `/${id}/objects/status`,
                method: "GET",
            }),
        }),
        connectGoogleAccount: builder.mutation<void, { token: string, objects: { [key: string]: string[] } }>({
            query: ({ token, objects }) => ({
                url: `/`,
                method: "PATCH",
                body: { objects },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
        }),
        createAuth: builder.mutation<void, { target: string, grant: GOOGLE_ANALYTICS_SCOPES[], wp_id: number }>({
            query: ({ target, grant, wp_id }) => ({
                url: `/create`,
                method: "POST",
                body: { target, grant, wp_id },
            }),
        }),
        resendAuth: builder.mutation<void, { id: number }>({
            query: ({ id }) => ({
                url: `/${id}/resend`,
                method: "POST",
            }),
        }),
        deleteAuth: builder.mutation<void, { id: number }>({
            query: ({ id }) => ({
                url: `/${id}`,
                method: "DELETE",
            }),
        }),
        GAdsConversions: builder.query<
            Array<GADSCampaignConversion>, { id: number, customer_ids?: string[], kind?: "SEARCH" | "SHOPPING" }>({
                query: ({ id, customer_ids, kind }) => {
                    return {
                        url: `/${id}/gads/conversions?${customer_ids ? `customer_ids=${customer_ids?.join('&customer_ids=')}` : ''}`,
                        method: "GET",
                    }
                },
            }),
        GA4Conversions: builder.query<
            GA4ConversionEvent[], { id: number, property_ids?: string[] }>({
                query: ({ id, property_ids }) => {
                    let url = `/${id}/ga4/conversions?`;
                    if (property_ids) {
                        url += `property_ids=${property_ids?.join('&property_ids=')}`;
                    }
                    return {
                        url,
                        method: "GET",
                    }
                },
            }),
        GA4Accounts: builder.query<{
            data: {
                ga4_property_id: string;
                ga4_property_name: string;
                account_id: string;
                account_name: string;
            }[];
            pagination: {
                next_page_token: string;
                has_next_page: boolean;
            };
        }, { id: number, page_token?: string, page_size?: number }>({
            query: ({ id, page_token, page_size }) => ({
                url: `/workspace/${id}/list/ga4`,
                method: "GET",
                params: { page_token, page_size },
            }),
        }),
        GSCAccounts: builder.query<string[], number>({
            query: (id) => ({
                url: `/workspace/${id}/list/search_console`,
                method: "GET",
            }),
        }),
        GAdsAccounts: builder.query<[string, string][], { wpId: number, name: string }>({
            query: ({ wpId, name }) => ({
                url: `/workspace/${wpId}/list/ads`,
                method: "GET",
                params: { name },
            }),
        }),
        GADSCampaigns: builder.query<GADSCampaign[], { id: number, customer_ids?: string[], kind?: "SEARCH" | "SHOPPING" | "ALL", match?:  string }>({
            query: ({ id, customer_ids, kind, match }) => ({
                url: `/${id}/gads/campaigns?${customer_ids ? `customer_ids=${customer_ids?.join('&customer_ids=')}` : ''}${kind ? `&kind=${kind}` : ''}${match ? `&match=${match}` : ''}`,
                method: "GET",
            }),
        }),
        GADSCampaignsMatching: builder.query<GADSCampaign[], { id: number, customer_ids?: string[], match?: string }>({
            query: ({ id, customer_ids, match }) => ({
                url: `/${id}/gads/campaigns/matching?${customer_ids ? `customer_ids=${customer_ids?.join('&customer_ids=')}` : ''}`,
                method: "PUT",
                body: match,
            }),
        }),
        GADSCampaignsRegions: builder.query<{ [campaign_id: string]: GADSCampaign[] }, { id: number, customer_ids?: string[], match?: any }>({
            query: ({ id, customer_ids, match }) => ({
                url: `/${id}/gads/campaigns/regions/validate?${customer_ids ? `customer_ids=${customer_ids?.join('&customer_ids=')}` : ''}`,
                method: "PUT",
                body: match,
            }),
        }),
    }),
});


export default googleAPI;

export const {
    useGetGoogleAccountQuery,
    useGetGoogleAuthListQuery,
    useConnectGoogleAccountMutation,
    useCreateAuthMutation,
    useDeleteAuthMutation,
    useLazyGetGoogleAuthListQuery,
    useGA4AccountsQuery,
    useGSCAccountsQuery,
    useGAdsAccountsQuery,
    useGAdsConversionsQuery,
    useGA4ConversionsQuery,
    useGADSCampaignsQuery,
    useGADSCampaignsMatchingQuery,
    useResendAuthMutation,
    useLazyGADSCampaignsRegionsQuery,
} = googleAPI;